import { useMemo } from 'react'

import { useRouter } from 'next/router'

import AnimateIntoView from '~/components/atoms/AnimateIntoView/AnimateIntoView'
import type { ArticleListItemProps } from '~/components/atoms/ArticleListItem/ArticleListItem'
import ArticleListItem from '~/components/atoms/ArticleListItem/ArticleListItem'
import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Col, Grid, Row } from '~/components/atoms/Grid'
import Spacer, { SpacerSizes } from '~/components/atoms/Spacer/Spacer'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'

import styles from './FeaturedArticles.module.css'

export interface FeaturedArticlesProps {
  articles: ArticleListItemProps[]
  title?: string | null
  theme?: ComponentTheme
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  testID?: string
  moduleId?: string
}

export default function FeaturedArticles({
  title,
  articles,
  theme = ComponentTheme.primary,
  verticalPadding,
  noPaddingBottom,
  testID,
  moduleId,
}: FeaturedArticlesProps) {
  const router = useRouter()

  const filteredArticles = useMemo(() => {
    if (router.query.category) {
      return articles.filter(article => {
        return (
          article?.primaryCategory &&
          article?.primaryCategory?.slug === (router.query.category as string)
        )
      })
    }

    if (router.query.author) {
      return articles.filter(article => {
        return (
          article?.expert &&
          article?.expert.name === (router.query.author as string)
        )
      })
    }

    return articles
  }, [articles, router.query.category, router.query.author])

  return (
    <Fixture
      theme={theme}
      verticalPadding={verticalPadding}
      noPaddingBottom={noPaddingBottom}
      id={moduleId}
    >
      <Container>
        <div className={styles.base} data-testid={testID}>
          {title && (
            <Text
              size={TextSizes.primaryTitleLrg}
              className={styles.title}
              Tag="h2"
            >
              {title}
            </Text>
          )}
          {filteredArticles.length === 0 && (
            <Text size={TextSizes.bodyLrg} className={styles.noArticles}>
              No articles found matching search...
            </Text>
          )}
          <Grid>
            <Row>
              {filteredArticles.map((article, idx) => (
                <Col
                  variant="col-1/2--sm col-1/3--lg col-1/4--hg"
                  key={`${article.title}-${idx}`}
                >
                  <AnimateIntoView>
                    <ArticleListItem {...article} />
                    <Spacer size={SpacerSizes.lg} />
                  </AnimateIntoView>
                </Col>
              ))}
            </Row>
          </Grid>
        </div>
      </Container>
    </Fixture>
  )
}
